import { Box, Text } from "@primer/react";
import { useEffect, useState } from "react";
import { useAppSelector } from "../hooks/useAppSelector";
import { formatCountdown, formatDateVi } from "../pages/classes/classesHelpers";

interface IRegistrationOpenCountdownProps {
    /** true = chiếm full chiều ngang. false (mặc định) = badge gọn. */
    block?: boolean;
    sx?: Record<string, unknown>;
}

/**
 * Bộ đếm ngược tới GIỜ MỞ ĐĂNG KÝ (openAt = tu_ngay1 của đợt) cho SV đang chờ trong cửa sổ
 * pre-open (cổng đã mở nhưng chưa tới thời gian đăng ký). Tự đọc redux queue → tự ẩn khi:
 *   - enforceTimeWindow=false (legacy, không gate thời gian), hoặc
 *   - chưa biết openAt (cache chưa có period / đợt chưa resolve).
 *
 * Về 0 → KHÔNG gọi API (tránh 50k SV bắn cùng lúc): server không phát event lại tại openAt,
 * nút Đăng ký ở /classes tự mở khoá theo Date.now() >= openAt. Ở đây chỉ hiện "đang mở" và để
 * QueueProcessor/AccessGranted đưa SV vào tiếp.
 *
 * Dùng chung QueueWaitingPage (chính) + LandingPage. Mirror pattern SaveCountdown.
 */
export const RegistrationOpenCountdown = ({ block, sx }: IRegistrationOpenCountdownProps) => {
    const { openAt, enforceTimeWindow } = useAppSelector(s => s.queue);
    const [now, setNow] = useState<number>(() => Date.now());

    const openTs = openAt ? new Date(openAt).getTime() : null;
    const valid = enforceTimeWindow && openTs != null && !Number.isNaN(openTs);
    const beforeOpen = valid && now < (openTs as number);

    useEffect(() => {
        // Chỉ tick khi còn trước giờ mở → tránh setInterval vĩnh viễn suốt phiên.
        if (!beforeOpen) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [beforeOpen]);

    if (!valid) return null;

    const remainMs = (openTs as number) - now;
    const opened = remainMs <= 0;

    const c = opened
        ? { bg: "#dafbe1", border: "#aceebb", fg: "#1a7f37", icon: "fa-door-open" }
        : { bg: "#ddf4ff", border: "#b6e3ff", fg: "#0969da", icon: "fa-hourglass-half" };

    return (
        <Box
            role="status"
            aria-live="polite"
            sx={{
                display: block ? "flex" : "inline-flex",
                alignItems: "center",
                justifyContent: block ? "center" : "flex-start",
                width: block ? "100%" : "auto",
                gap: 2,
                bg: c.bg,
                border: "1px solid",
                borderColor: c.border,
                borderRadius: 2,
                px: "12px",
                py: "8px",
                ...sx,
            }}
        >
            <i className={`fas ${c.icon}`} style={{ color: c.fg, fontSize: 14, flexShrink: 0 }} />
            <Text sx={{ fontSize: 1, fontWeight: 600, color: c.fg, lineHeight: 1.4 }}>
                {opened ? (
                    "Đã đến giờ đăng ký — đang chuẩn bị mở…"
                ) : (
                    <>
                        Đăng ký mở lúc <b>{formatDateVi(openAt)}</b> — còn <b>{formatCountdown(remainMs)}</b>.
                    </>
                )}
            </Text>
        </Box>
    );
};

export default RegistrationOpenCountdown;
